// Pure row-shaping for user-defined CALCULATED metrics (the `calc.` source).
// No cube/React deps so the trickiest runtime logic — multi-dataset operand
// resolution, date joins, YoY projection — is unit-testable; connect.ts
// resolves date keys / builds the sibling indexes' inputs and calls in.
import {
  availabilityWarningsForCalc,
  type AmazonFinancials,
  type AvailabilityIndex,
  calcDefsById,
  type CalculatedMetricDef,
  CUSTOM_KEY_PREFIX,
  evaluateCalcExpr,
  getComputedMetric,
  resolveComparisonDate,
  sourceForMetricKey,
  toFiniteNumber,
} from "~/shared/kpi"
import { type MetricDefinition } from "~/ui-rtk/constants/charts"
import { type CustomMetricSeries, type KpiRow } from "./custom-metric-rows"

// Sibling cube datasets for calculated-metric operands, each keyed by bucket
// date ('YYYY-MM-DD'). Calc metrics iterate the MAIN dataset's buckets, so
// operands living in the other datasets must join by DATE — the sibling
// queries can return fewer (or extra) rows than the main one, which makes row
// indices unsafe.
export type CalcSecondaryRows = Record<
  "brand_value" | "media_campaigns" | "initiatives",
  Map<string, Record<string, unknown>>
>

// Writes data-availability warnings for one bucket cell under the reserved
// `<weekKey>_warning` (current value) and `<weekKey>_warning_prev` (YoY
// sub-value) row keys. Reserved keys are safe: the table, export, chart and
// growth-row code all read column-keyed values only. The comparison cell is
// dated by the comparison bucket's own date (see the merge in queryTheCube).
export function annotateAvailability(
  row: KpiRow,
  weekKey: string,
  weekData: Record<string, unknown>,
  dateKey: string,
  availability: AvailabilityIndex,
  warningsAt: (bucketDate: string) => string | null,
) {
  const bucketDate = String(weekData[dateKey] ?? "")
  if (bucketDate.length < 10) return
  const warning = warningsAt(bucketDate)
  if (warning) row[`${weekKey}_warning`] = warning
  const comparisonDate = resolveComparisonDate(
    weekData,
    dateKey,
    bucketDate,
    availability.granularity,
    // Explicit-compare-window shift (undefined on the default YoY path) —
    // keeps the fallback date consistent with the index's own bounds.
    availability.comparisonStartOf,
  )
  const prevWarning = warningsAt(comparisonDate)
  if (prevWarning) row[`${weekKey}_warning_prev`] = prevWarning
}

/** Indexes a merged cube dataset by bucket date (date-only slice of `dateKey`). */
export function rowsByBucketDate(
  data: Array<Record<string, unknown>> | null,
  dateKey: string,
): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>()
  if (!data || data.length === 0) return map
  for (const row of data) {
    const date = String(row[dateKey] ?? "").slice(0, 10)
    if (date.length === 10) map.set(date, row)
  }
  return map
}

/**
 * Builds one sibling source's date-keyed row index for the calc-operand joins.
 * Merged rows join by their own bucket date. Main-dataset buckets the sibling
 * has NO current-window row for get a SYNTHETIC `compared.*`-only row built
 * from the sibling's raw comparison window (paired by the same YoY calendar
 * key the merge uses) — the sibling merge only emits rows for current-window
 * buckets, so without this a formula's YoY side reads 0 whenever the sibling
 * cube lags the main one (routine for brand value, whose freshness trails by
 * days). The synthetic row's current side stays absent → 0, matching the
 * cube-has-no-data convention.
 *
 * Both keying sides pass DATE-ONLY strings to `yoyKeyOf` (comparison rows with
 * window 'comparison', bucket starts with 'current' — mirroring the merge), so
 * the pairing is internally consistent at every granularity even though the
 * merge itself keys full timestamps.
 */
export function buildCalcSecondarySource(args: {
  mergedRows: Array<Record<string, unknown>> | null
  comparisonRows: Array<Record<string, unknown>> | null
  dateKey: string
  /** The MAIN dataset's bucket start dates ('YYYY-MM-DD'). */
  bucketStarts: string[]
  /** YoY calendar key for a date-only string (getYoyComparisonKey closure). */
  yoyKeyOf: (dateOnly: string, window: "current" | "comparison") => string
}): Map<string, Record<string, unknown>> {
  const { mergedRows, comparisonRows, dateKey, bucketStarts, yoyKeyOf } = args
  const map = rowsByBucketDate(mergedRows, dateKey)
  if (!comparisonRows || comparisonRows.length === 0) return map
  const missing = bucketStarts.filter(start => !map.has(start))
  if (missing.length === 0) return map

  // Latest year wins on a key collision, mirroring the merge's pick.
  const comparisonByYoyKey = new Map<
    string,
    { year: number; row: Record<string, unknown> }
  >()
  for (const row of comparisonRows) {
    const date = String(row[dateKey] ?? "").slice(0, 10)
    if (date.length !== 10) continue
    const key = yoyKeyOf(date, "comparison")
    const year = Number(date.slice(0, 4))
    const existing = comparisonByYoyKey.get(key)
    if (!existing || year > existing.year) {
      comparisonByYoyKey.set(key, { year, row })
    }
  }
  for (const start of missing) {
    const match = comparisonByYoyKey.get(yoyKeyOf(start, "current"))
    if (!match) continue
    const synthetic: Record<string, unknown> = {}
    for (const key of Object.keys(match.row)) {
      synthetic[`compared.${key}`] = match.row[key]
    }
    map.set(start, synthetic)
  }
  return map
}

// Row builder for user-defined CALCULATED metrics. Same shape/loop as
// processComputedMetrics in connect.ts, but the per-bucket value comes from
// evaluating the metric's stored expression AST (report config) rather than a
// registered compute fn. A leaf ref resolves via `makeRefGetter`: `metrics.*`
// reads the row column; `computed.*` runs that computed metric's own compute fn
// (which needs the same getter + Amazon settings); `custom.*` reads the
// pre-bucketed series by column index; brand-value / media-campaigns /
// initiative leaves read their sibling dataset's row joined by bucket date;
// nested `calc.*` leaves resolve through the report's defs inside
// evaluateCalcExpr (cycles / dangling ids → null). For YoY we project the
// getter onto each row's `compared.*` twins (every dataset carries its own
// merge). `data` is the merged revenueKPIData and `dateKey` its resolved
// `metrics.date` column, so a calc row always spans the main dataset's buckets.
export function processCalculatedMetrics(
  data: Array<Record<string, unknown>> | null,
  dateKey: string,
  settings: AmazonFinancials,
  metrics: MetricDefinition[],
  defsByKey: Map<string, CalculatedMetricDef>,
  customSeriesById: Map<string, CustomMetricSeries>,
  secondaryRows: CalcSecondaryRows,
  availability?: AvailabilityIndex,
): KpiRow[] {
  if (!data || data.length === 0 || metrics.length === 0) return []

  const sortedData = [...data].sort(
    (a, b) =>
      new Date(a[dateKey] as string).getTime() -
      new Date(b[dateKey] as string).getTime(),
  )

  // Nested calc.<id> leaves resolve against the report's full defs list (NOT
  // just the defs rendered in this group).
  const defsById = calcDefsById([...defsByKey.values()])

  // `index` aligns a custom.<id> leaf to its pre-bucketed value: custom values
  // are not columns on the row, they are index-aligned to the same sorted
  // buckets (columnBuckets is derived from this same sorted revenueKPIData).
  // `bucketDate` joins the sibling cube datasets by date.
  const makeRefGetter =
    (
      weekData: Record<string, unknown>,
      prefix: string,
      index: number,
      bucketDate: string,
    ) =>
    (key: string): number | null => {
      if (key.startsWith("computed.")) {
        const computed = getComputedMetric(key)
        if (!computed) return null
        return computed.compute(
          k => toFiniteNumber(weekData[`${prefix}${k}`]),
          settings,
        )
      }
      if (key.startsWith(CUSTOM_KEY_PREFIX)) {
        const series = customSeriesById.get(key.slice(CUSTOM_KEY_PREFIX.length))
        if (!series) return null
        // A bucket with no entered value means no data: 0, matching the
        // missing cube-column convention — a gap in a manually-entered series
        // must not null out the whole formula for that bucket. (Only a
        // dangling id — the metric itself deleted — is null, like a dangling
        // calc ref.) The custom metric's OWN row still renders gaps blank.
        return (
          (prefix === "compared."
            ? series.previous[index]
            : series.current[index]) ?? 0
        )
      }
      const source = sourceForMetricKey(key)
      if (
        source === "brand_value" ||
        source === "media_campaigns" ||
        source === "initiatives"
      ) {
        // A bucket the sibling cube has no row for means it has no data there:
        // 0, matching the metrics.* missing-column convention. The `compared.`
        // projection reads the sibling row's own YoY twins — both merges pair
        // comparison buckets by the same calendar key, so the sides agree
        // (comparison-only buckets arrive as synthetic compared.*-only rows,
        // see buildCalcSecondarySource).
        const row = secondaryRows[source].get(bucketDate)
        return row ? toFiniteNumber(row[`${prefix}${key}`]) : 0
      }
      // metrics.* (and any other row column) — missing coerces to 0.
      return toFiniteNumber(weekData[`${prefix}${key}`])
    }

  return metrics.map(metricDef => {
    const def = defsByKey.get(metricDef.key)
    const row: KpiRow = {
      metric: metricDef.label,
      type: metricDef.type,
      key: metricDef.key,
      suffix: metricDef.suffix,
    }

    sortedData.forEach((weekData, index) => {
      const weekKey = `week_${index + 1}`
      const bucketDate = String(weekData[dateKey] ?? "").slice(0, 10)
      const currentValue = def
        ? evaluateCalcExpr(
            def.expression,
            makeRefGetter(weekData, "", index, bucketDate),
            defsById,
          )
        : null
      const previousValue = def
        ? evaluateCalcExpr(
            def.expression,
            makeRefGetter(weekData, "compared.", index, bucketDate),
            defsById,
          )
        : null

      let periodChange: number | null = null
      if (previousValue !== null && previousValue !== 0) {
        periodChange =
          (((currentValue ?? 0) - previousValue) / previousValue) * 100
      } else if ((currentValue ?? 0) === 0 && (previousValue ?? 0) === 0) {
        periodChange = 0
      }

      row[weekKey] = currentValue
      row[`${weekKey}_yoy_previous`] = previousValue
      row[`${weekKey}_yoy_change`] = periodChange

      // A calculated metric combines several metrics, so warn whenever the
      // bucket falls outside any leaf source's available window (both sides),
      // expanding nested calc refs to their own leaves.
      if (availability && def) {
        annotateAvailability(row, weekKey, weekData, dateKey, availability, d =>
          availabilityWarningsForCalc(
            availability,
            def.expression,
            d,
            defsById,
          ),
        )
      }
    })

    return row
  })
}
